import React, { useEffect } from 'react'
import { useField, useFormikContext } from 'formik'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import mapEnumForSelect from '../../../utils/mapEnumForSelect'
import dayjs from 'dayjs'
import SelectField from '../../form/SelectField'

import isoWeek from 'dayjs/plugin/isoWeek'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
dayjs.extend(quarterOfYear)
dayjs.extend(isoWeek)

export enum Timeframe {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  THIS_DAY_LAST_WEEK = 'thisDayLastWeek',
  THIS_WEEK = 'thisWeek',
  THIS_MONTH = 'thisMonth',
  THIS_QUARTER = 'thisQuarter',
  THIS_YEAR = 'thisYear',
  LAST_3_DAYS = 'last3Days',
  LAST_WEEK = 'lastWeek',
  LAST_30_DAYS = 'last30Days',
  LAST_MONTH = 'lastMonth',
  LAST_60_DAYS = 'last60Days',
  LAST_QUARTER = 'lastQuarter',
  CUSTOM = 'custom',
}

export const DateFormat = 'YYYY-MM-DD'

export const computeTimeframeValues = (): Record<Timeframe, { startDate: string; endDate: string }> => ({
  [Timeframe.TODAY]: { startDate: dayjs().format(DateFormat), endDate: dayjs().format(DateFormat) },
  [Timeframe.YESTERDAY]: {
    startDate: dayjs().subtract(1, 'days').format(DateFormat),
    endDate: dayjs().subtract(1, 'days').format(DateFormat),
  },
  [Timeframe.LAST_3_DAYS]: {
    startDate: dayjs().subtract(2, 'days').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.THIS_DAY_LAST_WEEK]: {
    startDate: dayjs().subtract(7, 'days').format(DateFormat),
    endDate: dayjs().subtract(7, 'days').format(DateFormat),
  },
  [Timeframe.THIS_WEEK]: {
    startDate: dayjs().startOf('isoWeek').format(DateFormat),
    endDate: dayjs().endOf('isoWeek').format(DateFormat),
  },
  [Timeframe.LAST_WEEK]: {
    startDate: dayjs().subtract(7, 'days').startOf('isoWeek').format(DateFormat),
    endDate: dayjs().subtract(7, 'days').endOf('isoWeek').format(DateFormat),
  },
  [Timeframe.LAST_30_DAYS]: {
    startDate: dayjs().subtract(29, 'days').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.LAST_60_DAYS]: {
    startDate: dayjs().subtract(59, 'days').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.LAST_MONTH]: {
    startDate: dayjs().subtract(1, 'month').startOf('month').format(DateFormat),
    endDate: dayjs().subtract(1, 'month').endOf('month').format(DateFormat),
  },
  [Timeframe.THIS_MONTH]: {
    startDate: dayjs().startOf('month').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.THIS_QUARTER]: {
    startDate: dayjs().startOf('quarter').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.THIS_YEAR]: {
    startDate: dayjs().startOf('year').format(DateFormat),
    endDate: dayjs().format(DateFormat),
  },
  [Timeframe.LAST_QUARTER]: {
    startDate: dayjs().subtract(1, 'quarter').startOf('quarter').format(DateFormat),
    endDate: dayjs().subtract(1, 'quarter').endOf('quarter').format(DateFormat),
  },
  [Timeframe.CUSTOM]: {
    startDate: '',
    endDate: '',
  },
})

const fielName = 'timeframe'

const toDateFormat = (date: string): string => (date ? dayjs(date).format(DateFormat) : '')

export const getMatchingTimeframe = (startDate: string, endDate: string): Timeframe => {
  const normalizedStart = toDateFormat(startDate)
  const normalizedEnd = toDateFormat(endDate)
  const timeframeValues = computeTimeframeValues()

  const timeframe = (Object.keys(timeframeValues) as Timeframe[]).find(
    (key) =>
      key !== Timeframe.CUSTOM &&
      timeframeValues[key].startDate === normalizedStart &&
      timeframeValues[key].endDate === normalizedEnd
  )

  return timeframe ?? Timeframe.CUSTOM
}

export const DateRangeSelect: React.FC = (): JSX.Element => {
  const t = useAppTranslations()
  const formik = useFormikContext()
  const [{ value: startDate }] = useField({ name: 'startDate' })
  const [{ value: endDate }] = useField({ name: 'endDate' })

  useEffect(() => {
    const matchedTimeframe = getMatchingTimeframe(startDate, endDate)
    if (formik.values.timeframe !== matchedTimeframe) formik.setFieldValue('timeframe', matchedTimeframe)
  }, [startDate, endDate, formik.values.timeframe])

  const onChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    const {
      target: { value },
    } = event

    const selectedTimeframe = computeTimeframeValues()[value as Timeframe]
    if (value === Timeframe.CUSTOM) return

    formik.setFieldValue('startDate', selectedTimeframe.startDate)
    formik.setFieldValue('endDate', selectedTimeframe.endDate)
    formik.validateForm()
  }

  const timeframesOptions = mapEnumForSelect(Timeframe)
    .filter((type) => type.value !== Timeframe.CUSTOM)
    .map((type) => ({ value: type.value as Timeframe, label: t(`stats.filter.timeframeValues.${type.label}`) }))

  return (
    <SelectField
      name={fielName}
      label={t('stats.filter.timeframe')}
      variant="outlined"
      onChange={onChange}
      options={timeframesOptions}
    />
  )
}
